// Free Ebook landing — captures email/name → POST /api/lead-magnet/call-coach-ebook
// → on success shows download link + "check your inbox" success state, and
// enrolls the visitor in the 5-day Call Coach follow-up drip.
//
// Design intent: matches the iSpeedToClose marketing aesthetic (dark navy
// cover, signal-green accents) — borrows the visual language of the Landing
// page Hero + DemoModeBanner.

import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { track, captureUtm } from "@/lib/analytics";
import AssignFlowLogo from "@/components/AssignFlowLogo";
import MoreEbooksStrip from "@/components/MoreEbooksStrip";
import PlaybookSocialShare from "@/components/PlaybookSocialShare";
import {
  ArrowRight,
  Mail,
  User as UserIcon,
  Download,
  CheckCircle2,
  BookOpen,
  Brain,
  Headphones,
  Radio,
  Sparkles,
  Star,
  Lock,
  ChevronRight,
} from "lucide-react";

const backend = process.env.REACT_APP_BACKEND_URL;
const PDF_URL = `${backend}/api/lead-magnet/call-coach-ebook.pdf`;
const CAPTURE_URL = `${backend}/api/lead-magnet/call-coach-ebook`;

const CHAPTERS = [
  { n: "01", t: "Why most seller calls fail in the first 90 seconds" },
  { n: "02", t: "The four pillars: Rapport · Discovery · Objection · Close" },
  { n: "03", t: "Scoring the first 30 seconds" },
  { n: "04", t: "The only three discovery questions that matter" },
  { n: "05", t: "Naming objections before the seller does" },
  { n: "06", t: "The soft-anchor close that converts 27%" },
  { n: "07", t: "Three motivation tells most wholesalers miss" },
  { n: "08", t: "Anatomy of a 94/100 call — full transcript" },
  { n: "09", t: "Anatomy of a 38/100 call — and how to recover mid-flight" },
  { n: "10", t: "Your 30-day self-coaching cadence" },
];

const PROOF_POINTS = [
  { k: "25 pp", v: "of operator-grade content" },
  { k: "12,000+", v: "AI-graded calls behind the rubric" },
  { k: "2 trans-\ncripts", v: "annotated turn-by-turn" },
  { k: "5 emails", v: "follow-up coaching series" },
];

const REVIEW_AVATARS = [
  "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=faces&fit=crop&w=120&h=120&q=80",
  "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=faces&fit=crop&w=120&h=120&q=80",
  "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?crop=faces&fit=crop&w=120&h=120&q=80",
];

export default function FreeEbook() {
  useEffect(() => {
    captureUtm();
    track("free_ebook_view");
  }, []);

  return (
    <div className="mkt-shell bg-[#F8FAFC] min-h-screen">
      <EbookNav />
      <Hero />
      <ProofStrip />
      <Inside />
      <Testimony />
      <FinalCTA />
      <MoreEbooksStrip currentSlug="call-coach" />
      <Footer />
    </div>
  );
}

function EbookNav() {
  return (
    <header className="mkt-nav border-b border-[var(--line)]">
      <div className="mkt-nav-inner">
        <Link to="/" className="flex items-center gap-2" data-testid="ebook-brand">
          <img src="/brand/wordmark-color.png" alt="iSpeedToClose" className="h-14 md:h-16 w-auto" width="243" height="64" />
        </Link>
        <div className="hidden md:flex items-center gap-3">
          <Link to="/" className="btn-ghost" data-testid="ebook-back-landing">
            ← Back to home
          </Link>
          <Link to="/register" className="btn-money" data-testid="ebook-nav-register">
            Try free <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </header>
  );
}

function Hero() {
  const [form, setForm] = useState({ name: "", email: "" });
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const [captureId, setCaptureId] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    if (!form.email.includes("@") || form.name.trim().length < 1) {
      setErrorMsg("Please enter your name and a valid email.");
      return;
    }
    setStatus("submitting");
    track("free_ebook_submit_attempt");
    try {
      const r = await fetch(CAPTURE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          source: "free-ebook",
        }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setCaptureId(data.capture_id);
      setStatus("success");
      track("free_ebook_capture_success", { id: data.capture_id });
    } catch (err) {
      setStatus("error");
      setErrorMsg("Something went wrong — try again or use the direct download below.");
    }
  };

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-[#0B1220] via-[#0F172A] to-[#0B1220] text-white">
      {/* Atmospheric glows */}
      <div className="absolute -top-40 -right-40 w-[36rem] h-[36rem] rounded-full bg-[var(--primary)]/15 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-20 w-[30rem] h-[30rem] rounded-full bg-amber-400/8 blur-3xl pointer-events-none" />
      {/* Grid texture */}
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
        backgroundImage: "linear-gradient(rgba(16,185,129,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(16,185,129,0.6) 1px, transparent 1px)",
        backgroundSize: "44px 44px",
      }} />

      <div className="relative max-w-[1240px] mx-auto px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-[1.15fr_1fr] gap-14 items-center">
        {/* LEFT — pitch + form */}
        <div>
          <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-[var(--primary)] bg-[var(--primary)]/15 border border-[var(--primary)]/30 rounded-full px-3 py-1 mb-6">
            <BookOpen size={12} /> Free 25-page Operator Playbook
          </div>
          <h1 className="font-display font-black tracking-tight leading-[0.98] text-4xl sm:text-5xl lg:text-6xl mb-5">
            The <span className="text-[var(--primary)]">60-Second</span><br />
            Seller Call.
          </h1>
          <p className="text-base lg:text-lg text-white/75 leading-relaxed mb-7 max-w-xl">
            A wholesaler's scoring rubric for the only metric that matters — what comes out of your mouth on
            a seller call. Built from <b className="text-white">12,000+ AI-graded conversations</b> inside iSpeedToClose.
          </p>

          {/* Bullet teasers */}
          <ul className="space-y-2.5 mb-8 max-w-xl">
            {[
              "The 4-pillar rubric our AI uses to score every call",
              "Two annotated transcripts: 94/100 vs. 38/100 — full breakdown",
              "Preempt scripts for the four objections that kill 80% of deals",
              "A 30-day self-coaching cadence with a 60-second drill per week",
            ].map((b) => (
              <li key={b} className="flex items-start gap-3 text-sm lg:text-[15px] text-white/85">
                <span className="mt-[5px] w-4 h-4 rounded-full bg-[var(--primary)]/20 text-[var(--primary)] border border-[var(--primary)]/40 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 size={11} />
                </span>
                {b}
              </li>
            ))}
          </ul>

          {/* Capture form OR success state */}
          {status !== "success" ? (
            <form
              onSubmit={submit}
              className="bg-white/[0.04] backdrop-blur-sm border border-white/10 rounded-2xl p-5 max-w-xl"
              data-testid="ebook-capture-form"
            >
              <div className="grid sm:grid-cols-2 gap-3 mb-3">
                <label className="block">
                  <span className="sr-only">Your name</span>
                  <div className="relative">
                    <UserIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                      type="text"
                      required
                      placeholder="Your name"
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      className="w-full pl-9 pr-3 py-3 rounded-lg bg-white/[0.06] border border-white/15 text-white placeholder:text-white/40 outline-none focus:border-[var(--primary)]/60 text-sm"
                      data-testid="ebook-input-name"
                    />
                  </div>
                </label>
                <label className="block">
                  <span className="sr-only">Email</span>
                  <div className="relative">
                    <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                      type="email"
                      required
                      placeholder="you@yourbiz.com"
                      value={form.email}
                      onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                      className="w-full pl-9 pr-3 py-3 rounded-lg bg-white/[0.06] border border-white/15 text-white placeholder:text-white/40 outline-none focus:border-[var(--primary)]/60 text-sm"
                      data-testid="ebook-input-email"
                    />
                  </div>
                </label>
              </div>
              <button
                type="submit"
                disabled={status === "submitting"}
                className="w-full btn-money !py-3 !text-[15px] disabled:opacity-60"
                data-testid="ebook-submit"
              >
                {status === "submitting" ? "Sending…" : (
                  <>Send me the playbook <ArrowRight size={14} /></>
                )}
              </button>
              {errorMsg && (
                <div className="mt-3 text-xs text-rose-300" data-testid="ebook-error">{errorMsg}</div>
              )}
              <div className="mt-3 flex items-center gap-2 text-[11px] text-white/55">
                <Lock size={11} /> Name + email only · no credit card · unsubscribe any time
              </div>
            </form>
          ) : (
            <div
              className="bg-[var(--primary)]/12 border border-[var(--primary)]/35 rounded-2xl p-5 max-w-xl"
              data-testid="ebook-success"
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-[var(--primary)] text-[#0B1220] flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 size={18} />
                </div>
                <div className="flex-1">
                  <div className="font-display font-bold text-lg text-white mb-1">
                    Check your inbox.
                  </div>
                  <p className="text-sm text-white/75 leading-relaxed mb-3">
                    We just sent the PDF to <b className="text-white">{form.email}</b>. If it doesn't show up in a
                    minute, peek in spam — or grab it directly here:
                  </p>
                  <a
                    href={PDF_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-white text-[#0B1220] font-bold px-4 py-2.5 rounded-lg text-sm hover:bg-white/90 transition"
                    data-testid="ebook-direct-download"
                    onClick={() => track("free_ebook_direct_download", { id: captureId })}
                  >
                    <Download size={14} /> Download the PDF
                  </a>
                  <PlaybookSocialShare
                    playbookTitle="The 90-Second Call Coach"
                    onShare={(platform) => track("free_ebook_social_share", { platform, id: captureId })}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT — 3D-ish cover mockup */}
        <div className="hidden lg:block">
          <EbookCoverMockup />
        </div>
      </div>
    </section>
  );
}

// ─── Animated cover mockup (CSS-only, no extra assets) ──────────────────
function EbookCoverMockup() {
  return (
    <div className="relative w-full max-w-[420px] mx-auto">
      {/* Spine shadow */}
      <div
        className="absolute inset-y-6 -right-3 w-3 rounded-r-md bg-gradient-to-r from-black/40 to-black/0"
        aria-hidden
      />
      {/* Floor shadow */}
      <div
        className="absolute -bottom-10 left-6 right-6 h-10 bg-black/40 blur-2xl rounded-full"
        aria-hidden
      />
      {/* The cover — matches the actual PDF cover */}
      <div
        className="relative aspect-[0.77/1] rounded-2xl overflow-hidden ring-1 ring-white/10"
        style={{
          background: "linear-gradient(135deg, #0B1220 0%, #0F172A 55%, #0B1220 100%)",
          transform: "rotate(-3deg) translateX(-6px)",
          boxShadow: "0 30px 60px -20px rgba(0,0,0,0.7), 0 10px 25px -10px rgba(16,185,129,0.25)",
        }}
      >
        {/* Decorative glows */}
        <div
          className="absolute -top-20 -right-20 w-56 h-56 rounded-full opacity-50"
          style={{ background: "radial-gradient(circle, rgba(16,185,129,0.45), transparent 60%)" }}
          aria-hidden
        />
        <div
          className="absolute -bottom-24 -left-24 w-56 h-56 rounded-full opacity-30"
          style={{ background: "radial-gradient(circle, rgba(16,185,129,0.4), transparent 60%)" }}
          aria-hidden
        />
        {/* Accent stripe */}
        <div className="absolute inset-y-0 left-0 w-1 bg-[var(--primary)]" aria-hidden />
        {/* Cover content */}
        <div className="absolute inset-0 p-8 flex flex-col">
          <div className="flex items-center gap-2 text-white/90">
            <span className="w-7 h-7 rounded-md bg-white/10 flex items-center justify-center">
              <AssignFlowLogo size={14} />
            </span>
            <span className="font-display font-extrabold text-sm">iSpeedToClose</span>
          </div>
          <div className="text-[10px] tracking-[0.18em] text-white/45 mt-1 ml-9">
            OPERATOR PLAYBOOK · 2026
          </div>

          <div className="mt-auto">
            <div className="text-[11px] font-bold tracking-[0.18em] text-[var(--primary)] mb-3">
              OPERATOR PLAYBOOK · 25 PAGES
            </div>
            <div className="font-display font-black text-white tracking-tight leading-[0.95] text-[44px]">
              The<br />60-Second<br />Seller<br />Call.
            </div>
            <p className="mt-5 text-[12px] text-white/55 leading-snug max-w-[16rem]">
              A wholesaler's scoring rubric for the only metric that matters — what comes out of your mouth
              on a seller call. Built from thousands of AI-graded conversations.
            </p>
            <div className="mt-6 text-[10px] tracking-[0.16em] text-white/35 italic">
              WRITTEN FOR WHOLESALERS · AI-GRADED · FREE
            </div>
          </div>
        </div>
      </div>

      {/* Floating "AI graded" badge */}
      <div className="absolute -top-3 -left-3 bg-white rounded-full shadow-xl px-4 py-2 flex items-center gap-2 ring-1 ring-black/5"
           style={{ transform: "rotate(-8deg)" }}>
        <Sparkles size={14} className="text-[var(--primary-dark)]" />
        <span className="text-[12px] font-bold text-[var(--ink)]">AI-graded rubric</span>
      </div>
      {/* Floating "free" sticker */}
      <div className="absolute -bottom-4 -right-2 bg-[var(--primary)] text-[#0B1220] rounded-full shadow-xl px-4 py-2 flex items-center gap-2 ring-2 ring-[var(--primary)]/30"
           style={{ transform: "rotate(6deg)" }}>
        <span className="text-[11px] font-black tracking-widest uppercase">100% free</span>
      </div>
    </div>
  );
}

// ─── Proof strip ────────────────────────────────────────────────────────
function ProofStrip() {
  return (
    <section className="bg-white border-y border-[var(--line)]" data-testid="ebook-proof-strip">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
        {PROOF_POINTS.map((p) => (
          <div key={p.v} className="text-center md:text-left">
            <div className="font-display font-black text-3xl lg:text-4xl tracking-tighter text-[var(--ink)] whitespace-pre-line">
              {p.k}
            </div>
            <div className="text-xs lg:text-sm text-[var(--ink-2)] mt-1 leading-snug">{p.v}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── Inside section — chapter list + visual ─────────────────────────────
function Inside() {
  return (
    <section className="bg-[var(--bg)] py-20 lg:py-24" data-testid="ebook-inside">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-8">
        <div className="grid md:grid-cols-[1fr_1.3fr] gap-12 items-start">
          <div className="md:sticky md:top-24">
            <div className="mkt-eyebrow">Inside the playbook</div>
            <h2 className="mkt-h2 mb-4">Ten short chapters. Zero theory.</h2>
            <p className="text-[var(--ink-2)] text-base lg:text-lg leading-relaxed mb-6">
              Each chapter has a scoring rubric, two real transcript snippets — one A-grade,
              one D-grade — and a verbatim "next-best line" you can steal for your next call.
            </p>

            {/* Pillar mini-grid */}
            <div className="grid grid-cols-2 gap-2">
              <PillarChip icon={Radio} label="Rapport" pct="30% weight" />
              <PillarChip icon={Sparkles} label="Discovery" pct="35% weight" accent />
              <PillarChip icon={Brain} label="Objection" pct="20% weight" />
              <PillarChip icon={Headphones} label="Close" pct="15% weight" />
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-[var(--line)] shadow-sm overflow-hidden">
            {CHAPTERS.map((c, i) => (
              <div
                key={c.n}
                className={`flex items-center gap-4 px-5 py-4 ${i !== CHAPTERS.length - 1 ? "border-b border-[var(--line)]" : ""}`}
                data-testid={`ebook-chapter-${c.n}`}
              >
                <span className="font-display font-black text-[var(--primary-dark)] text-lg tracking-tighter w-10 flex-shrink-0">
                  {c.n}
                </span>
                <span className="font-medium text-[var(--ink)] text-sm lg:text-[15px] flex-1 leading-snug">
                  {c.t}
                </span>
                <ChevronRight size={14} className="text-[var(--ink-3)] flex-shrink-0" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function PillarChip({ icon: Icon, label, pct, accent }) {
  return (
    <div className={`rounded-xl border px-3 py-3 flex items-center gap-2.5 ${accent ? "bg-[var(--primary)]/10 border-[var(--primary)]/30" : "bg-white border-[var(--line)]"}`}>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${accent ? "bg-[var(--primary)] text-[#0B1220]" : "bg-[var(--ink)] text-white"}`}>
        <Icon size={14} />
      </div>
      <div className="min-w-0">
        <div className="text-[13px] font-bold text-[var(--ink)] truncate">{label}</div>
        <div className="text-[10px] uppercase tracking-widest text-[var(--ink-3)]">{pct}</div>
      </div>
    </div>
  );
}

// ─── Testimony ──────────────────────────────────────────────────────────
function Testimony() {
  return (
    <section className="bg-white py-20 lg:py-24 border-y border-[var(--line)]" data-testid="ebook-testimony">
      <div className="max-w-[920px] mx-auto px-6 lg:px-8 text-center">
        <div className="flex justify-center gap-1 mb-5">
          {[0, 1, 2, 3, 4].map((i) => <Star key={i} size={16} className="text-[var(--primary)]" fill="currentColor" />)}
        </div>
        <p className="font-display font-bold text-2xl lg:text-3xl leading-[1.25] tracking-tight text-[var(--ink)]">
          "The seller doesn't decide whether to sell to you in the close. They decide in the first 90 seconds.
          The rest of the call is just them either looking for an excuse to say yes — or looking for
          an excuse to hang up."
        </p>
        <div className="mt-8 flex items-center justify-center gap-3">
          <div className="flex -space-x-2">
            {REVIEW_AVATARS.map((src, i) => (
              <img key={i} src={src} alt="" className="w-9 h-9 rounded-full border-2 border-white object-cover" />
            ))}
          </div>
          <div className="text-left text-sm text-[var(--ink-2)]">
            From operator interviews —<br />
            <span className="font-semibold text-[var(--ink)]">12,000+ graded calls</span> behind the data
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Final CTA ──────────────────────────────────────────────────────────
function FinalCTA() {
  return (
    <section className="mkt-section" data-testid="ebook-final-cta">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[var(--ink)] via-[#0F172A] to-[#0B1220] text-white p-10 lg:p-16">
        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[var(--primary)]/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-amber-400/10 blur-3xl pointer-events-none" />
        <div className="relative max-w-2xl">
          <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-[var(--primary)] bg-[var(--primary)]/15 border border-[var(--primary)]/30 rounded-full px-3 py-1 mb-5">
            <Brain size={11} /> Want the AI to grade your real calls?
          </div>
          <h2 className="font-display font-black text-3xl lg:text-4xl tracking-tight leading-[1.05] mb-4">
            Stop self-grading.<br />Let the AI do it.
          </h2>
          <p className="text-white/75 text-base lg:text-lg leading-relaxed mb-7">
            Every call you place through iSpeedToClose is auto-recorded, transcribed by Whisper, and
            graded by Claude across the exact rubric in this playbook — within 60 seconds of hangup.
            Free 14-day trial, no credit card.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/register?utm_source=free_ebook_cta" className="btn-money !text-[15px]" data-testid="ebook-final-cta-register">
              Start free <ArrowRight size={14} />
            </Link>
            <a href={PDF_URL} target="_blank" rel="noopener noreferrer"
               className="btn-ghost text-white border-white/30 hover:bg-white/10"
               data-testid="ebook-final-cta-download">
              <Download size={14} /> Just give me the PDF
            </a>
          </div>
        </div>
      </div>

      {/* Sister-ebook cross-link */}
      <div className="mt-10 max-w-2xl mx-auto px-2">
        <Link
          to="/tcpa-guide"
          className="block group bg-white border border-[var(--line)] hover:border-rose-300 rounded-2xl p-5 transition"
          data-testid="ebook-tcpa-crosslink"
        >
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-rose-100 text-rose-700 flex items-center justify-center shrink-0 mt-0.5">
              <Lock size={18} />
            </div>
            <div className="flex-1">
              <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-rose-700 mb-1">Also defensive · Free 14 pp</div>
              <div className="font-display font-extrabold text-lg lg:text-xl text-[var(--ink)] leading-tight">
                Want the &quot;don&apos;t get sued&quot; companion?
              </div>
              <div className="text-sm text-[var(--ink-2)] mt-1 leading-relaxed">
                Our <b>TCPA Survival Guide</b> covers how to send 10,000+ texts without triggering
                $500–$1,500-per-message lawsuits — real case settlements + the 5-layer defensive
                stack.
              </div>
            </div>
            <ArrowRight size={18} className="text-[var(--ink-3)] group-hover:text-rose-700 group-hover:translate-x-0.5 transition shrink-0 mt-1" />
          </div>
        </Link>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-[var(--ink)] text-white/70 py-10 mt-10">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-4 text-sm">
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-md bg-white/10 flex items-center justify-center">
            <AssignFlowLogo size={14} />
          </span>
          <span className="font-display font-bold text-white">iSpeedToClose</span>
          <span className="text-white/40">·  the operating system for wholesalers</span>
        </div>
        <div className="flex items-center gap-5 text-xs">
          <Link to="/" className="hover:text-white">Home</Link>
          <Link to="/compare" className="hover:text-white">Compare</Link>
          <Link to="/legal/privacy" className="hover:text-white">Privacy</Link>
          <Link to="/legal/terms" className="hover:text-white">Terms</Link>
        </div>
      </div>
    </footer>
  );
}
